import { Component } from 'react';

// Route pages are code-split (React.lazy). When a deploy replaces the hashed
// chunk files, a tab opened before the deploy requests chunk URLs that no
// longer exist and the import rejects. Without a boundary React unmounts the
// whole tree and the user sees a blank page. This boundary reloads once to
// pick up the new build, and shows a recovery card for everything else.
const CHUNK_ERROR_PATTERN =
  /Loading (?:CSS )?chunk [^\s]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i;
const RELOAD_GUARD_KEY = 'domainoptic-chunk-reload-at';
const RELOAD_GUARD_WINDOW_MS = 60000;

export const isChunkLoadError = (error) =>
  Boolean(error) &&
  (error.name === 'ChunkLoadError' ||
    CHUNK_ERROR_PATTERN.test(String(error && error.message ? error.message : '')));

const markAutoReload = () => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      return false;
    }
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    return true;
  } catch (storageError) {
    // Without the guard an automatic reload could loop, so fall back to the
    // manual recovery card instead.
    return false;
  }
};

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error) && markAutoReload()) {
      this.setState({ reloading: true });
      window.location.reload();
    }
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    if (this.state.reloading) {
      return (
        <div className="mx-auto max-w-lg px-4 py-24 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Loading the newest version of DomainOptic...
          </p>
        </div>
      );
    }

    const stale = isChunkLoadError(this.state.error);

    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          This page could not load
        </h1>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
          {stale
            ? 'A new version of DomainOptic was published while this tab was open. Reload to get the latest version.'
            : 'Something went wrong while rendering this page. Reload to try again.'}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 rounded-lg bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-800"
        >
          Reload page
        </button>
      </div>
    );
  }
}

export default AppErrorBoundary;
